import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Slider } from './ui/slider';
import { User, Phone, GraduationCap, Lightning, MapPin } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
    { value: 'economy',              label: 'Economy Class',              base: 25,   group: 'normaal' },
    { value: 'business',             label: 'Business Class',             base: 35,   group: 'normaal' },
    { value: 'first_class',          label: 'First Class',                base: 45,   group: 'normaal' },
    { value: 'spoedopleiding_5',     label: 'Spoedcursus 5 uur',          base: 5,    group: 'spoed' },
    { value: 'spoedopleiding_10',    label: 'Spoedcursus 10 uur',         base: 10,   group: 'spoed' },
    { value: 'spoedopleiding_16_5',  label: 'Spoedcursus 16,5 uur',       base: 16.5, group: 'spoed' },
    { value: 'spoedopleiding_25',    label: 'Spoedcursus 25 uur',         base: 25,   group: 'spoed' },
];

function formatHours(n) {
    if (!n) return '0 uur';
    const isHalf = n % 1 !== 0;
    return `${isHalf ? n.toString().replace('.', ',') : n} uur`;
}

/**
 * Full-screen, non-dismissable modal that a self-registered student sees on
 * their very first login. Collects: naam, telefoon, instructeur, pakket +
 * extra-lessen slider. On submit we create their `students` doc and push the
 * instructor about the new signup.
 */
export default function StudentOnboardingModal({ user, onCompleted }) {
    const [instructors, setInstructors] = useState([]);
    const [form, setForm] = useState({
        name: user?.name || '',
        phone: '',
        address: '',
        instructor_id: '',
        package_type: 'economy',
        extra_lessons: 0,
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        axios.get(`${API}/instructors/public`, { withCredentials: true })
            .then(({ data }) => {
                setInstructors(data || []);
                setForm(prev => ({
                    ...prev,
                    instructor_id: prev.instructor_id || (data?.[0]?.id || ''),
                }));
            })
            .catch(() => {});
    }, []);

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const submit = async () => {
        if (busy) return;
        if (!form.name.trim())        return toast.error('Vul je naam in');
        if (!form.instructor_id)      return toast.error('Kies je rijinstructeur');
        if (!form.package_type)       return toast.error('Kies een lespakket');

        setBusy(true);
        try {
            await axios.post(`${API}/students/me/onboarding`, form, { withCredentials: true });
            toast.success('Welkom bij AutoPilot! 🎉');
            if (onCompleted) onCompleted();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Aanmelden mislukt');
        } finally {
            setBusy(false);
        }
    };

    const selectedPkg = PACKAGES.find(p => p.value === form.package_type);
    const totalLessons = (selectedPkg?.base || 0) + Number(form.extra_lessons || 0);

    return (
        // NOTE: no `onOpenChange` handler on the dialog → user cannot dismiss without submitting
        <Dialog open={true}>
            <DialogContent
                className="bg-[#1A202C] border-2 border-[#D4A537] text-white max-w-lg p-0 gap-0 overflow-hidden max-h-[92vh]"
                data-testid="student-onboarding-modal"
                hideClose
            >
                <div className="px-5 py-4 bg-gradient-to-br from-[#D4A537]/20 via-[#D4A537]/5 to-transparent border-b border-[#4A5568]">
                    <div className="flex items-center gap-2">
                        <GraduationCap size={26} weight="fill" className="text-[#D4A537]" />
                        <DialogTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Welkom! Vul je gegevens in
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-xs text-gray-300 mt-1">
                        Deze stap is nodig zodat je rijinstructeur je kan koppelen aan een leskaart.
                    </DialogDescription>
                </div>

                <div className="px-5 py-4 space-y-4 overflow-y-auto">
                    {/* Name */}
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-400 flex items-center gap-1.5 mb-1">
                            <User size={12} weight="fill" /> Volledige naam
                        </Label>
                        <Input
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            placeholder="Voor- en achternaam"
                            className="bg-[#2D3748] border-[#4A5568] text-white"
                            data-testid="onboarding-name"
                        />
                    </div>

                    {/* Phone */}
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-400 flex items-center gap-1.5 mb-1">
                            <Phone size={12} weight="fill" /> Telefoonnummer (optioneel)
                        </Label>
                        <Input
                            value={form.phone}
                            onChange={(e) => set('phone', e.target.value)}
                            placeholder="06-12345678"
                            className="bg-[#2D3748] border-[#4A5568] text-white"
                            data-testid="onboarding-phone"
                        />
                    </div>

                    {/* Address */}
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-400 flex items-center gap-1.5 mb-1">
                            <MapPin size={12} weight="fill" /> Adres (voor ophaal)
                        </Label>
                        <Input
                            value={form.address}
                            onChange={(e) => set('address', e.target.value)}
                            placeholder="Straatnaam 12, 1234 AB Amsterdam"
                            className="bg-[#2D3748] border-[#4A5568] text-white"
                            data-testid="onboarding-address"
                        />
                    </div>

                    {/* Instructor picker (only shows if more than 1 instructor) */}
                    {instructors.length > 1 && (
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">
                                Kies je rijinstructeur
                            </Label>
                            <select
                                value={form.instructor_id}
                                onChange={(e) => set('instructor_id', e.target.value)}
                                className="w-full bg-[#2D3748] border border-[#4A5568] text-white rounded-md px-3 py-2 text-sm"
                                data-testid="onboarding-instructor"
                            >
                                {instructors.map(i => (
                                    <option key={i.id} value={i.id}>{i.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Package picker */}
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">
                            Welk pakket wil je?
                        </Label>
                        <select
                            value={form.package_type}
                            onChange={(e) => set('package_type', e.target.value)}
                            className="w-full bg-[#2D3748] border border-[#4A5568] text-white rounded-md px-3 py-2 text-sm"
                            data-testid="onboarding-package"
                        >
                            <optgroup label="Standaard">
                                {PACKAGES.filter(p => p.group === 'normaal').map(p => (
                                    <option key={p.value} value={p.value}>{p.label} — {p.base} lessen</option>
                                ))}
                            </optgroup>
                            <optgroup label="Spoedcursus">
                                {PACKAGES.filter(p => p.group === 'spoed').map(p => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </optgroup>
                        </select>
                    </div>

                    {/* Extra lessons slider */}
                    <div className="bg-[#1A202C] rounded-lg p-3 border border-[#4A5568]">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
                                    <Lightning size={12} weight="fill" className="text-[#D4A537]" />
                                    Extra lessen bovenop pakket
                                </div>
                                <div className="text-[11px] text-gray-500 mt-0.5">Optioneel — meer oefening voor examen</div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-[#D4A537] tabular-nums" data-testid="onboarding-extra-value">
                                    +{formatHours(form.extra_lessons)}
                                </div>
                                <div className="text-[10px] text-gray-500">
                                    Totaal: <span className="text-white">{formatHours(totalLessons)}</span>
                                </div>
                            </div>
                        </div>
                        <Slider
                            min={0}
                            max={20}
                            step={0.5}
                            value={[Number(form.extra_lessons)]}
                            onValueChange={(v) => set('extra_lessons', v[0])}
                            className="flex-1 [&>span:first-child]:h-2 [&>span:first-child]:bg-[#4A5568] [&_[role=slider]]:bg-[#D4A537] [&_[role=slider]]:border-[#D4A537] [&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&>span:first-child_span]:bg-[#D4A537]"
                            data-testid="onboarding-extra-slider"
                        />
                        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                            <span>0</span>
                            <span>10</span>
                            <span>20</span>
                        </div>
                    </div>
                </div>

                <div className="p-3 border-t border-[#4A5568] bg-[#1A202C]">
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="w-full bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-bold py-3"
                        data-testid="onboarding-submit"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                        {busy ? 'Aanmelden…' : 'AutoPilot activeren →'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
